/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  SearchBar,
  Carousel,
  Button,
  Dropdown,
  Icon,
  Close,
  Actionable,
  Divider,
} from '@az/starc-ui';
import styles from './styles.module.scss';
import RatingFilter from '../RatingFilter';
import CloseIcon from '@/public/images/close.svg';
import { useLabels } from '@/hooks/useLabels';
import { useReviewFilters } from '../../context/ReviewFiltersProvider';
import { formatNumber } from '../../utils/formatNumber';
import { useBvPixelTracking } from '../../hooks/useBvPixelTracking';
import SortByDropDown from './SortByDropdown';
import cx from 'classnames';

type SortAttributeType = 'MOST_RECENT' | 'MOST_HELPFUL' | 'MOST_RELEVANT';

type ReviewsFilterProps = {
  onVerifiedFilter?: (verified: boolean) => void;
  onSortChange?: (value: string) => void;
  onSearchChange?: (value: string) => void;
  initialSelectedFilters?: number[];
  initialVerifiedSelected?: boolean;
  totalReviews?: number;
};

const sortOptions: Array<{ label: string; value: SortAttributeType }> = [
  {
    label: 'Most Recent',
    value: 'MOST_RECENT',
  },
  {
    label: 'Most Helpful',
    value: 'MOST_HELPFUL',
  },
];

const labelMap = {
  searchResultsSearch: 'label_Search_Results_Search',
  searchTopicsAndReviews: 'label_search_topics_and_reviews',
  star: 'label_reviews_star',
  stars: 'label_reviews_stars',
  singleReview: 'label_single_review',
  singleRecommendation: 'label_single_recommendation',
  reviews: 'label_reviews',
};

const ReviewsFilter = ({
  onVerifiedFilter,
  onSortChange,
  onSearchChange,
  initialVerifiedSelected = false,
  totalReviews,
}: ReviewsFilterProps) => {
  const { selectedRatings, setSelectedRatings } = useReviewFilters();

  const [open, setOpen] = useState(false);
  const [isVerifiedSelected, setIsVerifiedSelected] = useState(initialVerifiedSelected);
  const [searchText, setSearchText] = useState('');
  const labels = useLabels(labelMap);
  const { trackEvent } = useBvPixelTracking();
  const [isSearchBarActive, setIsSearchBarActive] = useState(false);
  const reviewCount = Number(totalReviews);
  const formattedReviewCount = formatNumber(reviewCount);

  const resetFilters = useCallback(() => {
    setSelectedRatings([]);
    setIsVerifiedSelected(false);
    setSearchText('');
  }, [setSelectedRatings]);

  useEffect(() => {
    resetFilters();
  }, [resetFilters]);

  useEffect(() => {
    const handleFocusChange = () => {
      setIsSearchBarActive(document?.activeElement?.id === 'reviewsSearchBar');
    };

    handleFocusChange();

    document.addEventListener('focusin', handleFocusChange);
    document.addEventListener('focusout', handleFocusChange);

    return () => {
      document.removeEventListener('focusin', handleFocusChange);
      document.removeEventListener('focusout', handleFocusChange);
    };
  }, []);

  const handleRatingFilter = (star: number) => {
    const newFilters = selectedRatings.includes(star)
      ? selectedRatings.filter((s) => s !== star)
      : [...selectedRatings, star];

    setSelectedRatings(newFilters);
    trackEvent('Filter By', 'Click', 'Filter', star);
  };

  const handleVerifiedFilter = () => {
    const newValue = !isVerifiedSelected;
    setIsVerifiedSelected(newValue);
    onVerifiedFilter?.(newValue);
    !isVerifiedSelected ? trackEvent('Verified Purchase', 'Click', 'Filter') : null;
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
  };

  const handleClearSearch = () => {
    setSearchText('');
    onSearchChange?.('');
  };

  const handleSearch = (event?: FormEvent) => {
    event?.preventDefault();
    onSearchChange?.(searchText);
    trackEvent('Search', 'Click', 'Search', searchText);
  };

  const searchButtons =
    isSearchBarActive || searchText.length > 0 ? (
      <>
        {searchText.length > 0 && (
          <Button
            onClick={handleClearSearch}
            className={styles.clearButton}
            variant="ghost"
            data-testid="review-clear-search-button"
          >
            <CloseIcon className={styles.clearButtonImg} />
          </Button>
        )}
        <div className={styles.searchButtonsDivider}>
          <div className={styles.verticalDivider} />
          <Button
            className={styles.searchBtn}
            data-testid="review-search-button"
            variant="ghost"
            type="submit"
          >
            <Text variant="text-link" className={styles.searchBtnText}>
              {labels.searchResultsSearch}
            </Text>
          </Button>
        </div>
      </>
    ) : undefined;

  return (
    <div className={styles.reviewsFilterContainer}>
      <div className={styles.filtersContainer}>
        <form className={styles.searchBarContainer} onSubmit={handleSearch}>
          <Text variant="subtitle-bold" weight="bold" className={styles.searchBarText}>
            Looking for something specific?
          </Text>
          <SearchBar
            label={labels.searchTopicsAndReviews}
            className={cx(
              styles.searchBar,
              isSearchBarActive && searchText.length === 0
                ? styles.emptyInputStyles
                : searchText.length > 0 && styles.inputStyles
            )}
            onValueChange={handleSearchChange}
            value={searchText}
            endElement={searchButtons}
            id="reviewsSearchBar"
          />
        </form>
        <div className={styles.reviewFilters}>
          <Carousel className={styles.reviewCarousel} hideControls>
            <div className={styles.dropdownWrapper}>
              <Text variant="subtitle-bold" weight="bold" className={styles.reviewFilterText}>
                Sort by
              </Text>
              <SortByDropDown sortOptions={sortOptions} onSortChange={onSortChange} />
            </div>
            <div className={styles.dropdownWrapper}>
              <Text variant="subtitle-bold" weight="bold" className={styles.reviewFilterText}>
                Filter by
              </Text>
              <Dropdown
                placement="bottom-end"
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                className={styles.reviewDropdown}
              >
                <Dropdown.Button className={styles.dropdownButton}>
                  <Text className={styles.dropdownButtonText}>All Ratings</Text>
                </Dropdown.Button>
                <Dropdown.Content className={styles.dropdownContent}>
                  {open && (
                    <div className={styles.dropdownContent}>
                      <RatingFilter
                        handleFilter={handleRatingFilter}
                        selectedRatings={selectedRatings}
                      />
                    </div>
                  )}
                </Dropdown.Content>
              </Dropdown>
            </div>

            <Actionable
              onClick={handleVerifiedFilter}
              className={isVerifiedSelected ? styles.selectedButton : styles.reviewButton}
            >
              Verified Purchase
            </Actionable>
          </Carousel>
        </div>
      </div>
      <div className={styles.activeFiltersContainer}>
        {selectedRatings.map((star) => (
          <Button key={star} variant="secondary" className={styles.reviewStarButton}>
            <View direction={'row'} justify={'center'}>
              <Text variant="display-5" className={styles.reviewStarText}>
                {`${star} ${star === 1 ? labels.star : labels.stars}`}
              </Text>
              <Actionable
                onClick={() => {
                  const newFilters = selectedRatings.filter((s) => s !== star);
                  setSelectedRatings(newFilters);
                }}
              >
                <Icon svg={Close} size={5} className={styles.reviewStarIcon} />
              </Actionable>
            </View>
          </Button>
        ))}
        {selectedRatings.length > 0 && (
          <View.Item>
            <Button
              variant="primary"
              onClick={() => {
                setSelectedRatings([]);
              }}
              className={styles.clearAllButton}
            >
              <Text variant="display-5" className={styles.clearAllText}>
                Clear All
              </Text>
            </Button>
          </View.Item>
        )}
      </div>
      <Text
        variant="main-body"
        className={styles.totalReviewsText}
        attributes={{ id: 'reviews-list' }}
      >
        {`${formattedReviewCount} ${reviewCount === 1 ? labels.singleReview : labels.reviews}`}
      </Text>

      <Divider className={styles.horizontalDivider} />
    </div>
  );
};

export default ReviewsFilter;
